import { Row, Col, Card, Typography, Button, Layout } from "antd";
import {
  GlobalOutlined,
  CloudServerOutlined,
  DeploymentUnitOutlined,
  SecurityScanOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import CustomHeader from "../Header";
import Footer from "../Footer";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { scrollToElementById } from "../../../Utility/scrollUtils";
import image1 from "../../../Utility/images/inventory/image-1.webp";
import invBanner from "../../../Utility/images/inventory/inv-banner.webp";
import inventoryBanner from "../../../Utility/images/inventory/inventoryBanner.webp";
import featured from "../../../Utility/images/inventory/featured.webp";
import inventoryControl from "../../../Utility/images/inventory/inventory-control.webp";
import "./InventoryFeature.css";
import { Helmet } from "react-helmet-async";

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;

const multipleStock = [
  "Create multiple stores and manage stocks as per store.",
  "Get alerts and notifications for low stocks as per store.",
  "Get stock valuation as per store.",
  "Negative stocks restriction as per store.",
  "Realtime update of stocks as per document creation.",
];

const physicalStock = [
  "Update product stocks by checking physical stock as per stores.",
  "Manually update stocks for adjustments as per store.",
];

const customField = [
  "Create custom fields on item level and fetch it on documents.",
  "Manage multiple units for single items and get realtime stocks as per different units.",
];

const stockHistory = [
  "Get realtime item level history as per stores and units.",
  "Store level history for better tracking.",
];

const services = [
  {
    title: "Manage Multiple Stores",
    icon: <CloudServerOutlined style={{ fontSize: "40px", color: "#000" }} />,
    color: "#FFD3B6",
  },
  {
    title: "Physical Stock Reconciliation",
    icon: (
      <DeploymentUnitOutlined style={{ fontSize: "40px", color: "#000" }} />
    ),
    color: "#A8E6CF",
  },
  {
    title: "Custom Fields and Alternate Units",
    icon: <GlobalOutlined style={{ fontSize: "40px", color: "#000" }} />,
    color: "#D4A5A5",
  },
  {
    title: "Stock History",
    icon: <SecurityScanOutlined style={{ fontSize: "40px", color: "#000" }} />,
    color: "#91C4F2",
  },
];

const InventoryFeature = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToElementById("inventoryFeature");
  }, [pathname]);

  return (
    <div id="inventoryFeature">
      <Helmet>
        <title>Inventory Management Software Solutions | Ease Margin</title>
        <meta
          name="description"
          content="Boost efficiency with EaseMargin’s Inventory Management Software. Track stock, reduce errors, and streamline operations for smarter business management."
        />
        <link
          rel="canonical" href="https://easemargin.com/services/inventory-management" />
      </Helmet>

      <CustomHeader />
      <Layout className="inventoryFeature-layout">
        <Header className="inventoryFeature-header">
          <div className="inventoryFeature-header-inner">
            <Title level={1} className="inventoryFeature-title-large">
              Smart Inventory Management for Growing Businesses
            </Title>
            <Paragraph className="inventoryFeature-paragraph-header">
              Automate stock tracking, manage orders, and optimize supply chain
              operations with AI-driven inventory control.
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request a Demo
              </Button>
            </Link>
          </div>
        </Header>
        <Content className="inventoryFeature-content">
          <Row gutter={[24, 24]} className="mt-40">
            <Col xs={24} sm={24} md={12} lg={12}>
              <Title level={2}>
                Monitor stock levels with live updates. Get automated alerts for
                low inventory and prevent overstocking.
              </Title>
              <Paragraph style={{ fontSize: "15px" }}>
                Efficiently manage your inventory with real-time stock level
                tracking. Our system continuously updates stock availability
                across all locations, ensuring you have the latest data at your
                fingertips. With live updates, you can quickly identify
                fast-moving products, prevent stock shortages, and optimize your
                inventory strategy.
              </Paragraph>
              <Paragraph style={{ fontSize: "15px" }}>
                Get automated alerts when stock levels drop below predefined
                thresholds, helping you take timely action before running out of
                essential items. Additionally, our system prevents overstocking
                by providing insights into demand trends, allowing you to make
                informed purchasing decisions. By reducing excess inventory and
                preventing stockouts, you can improve cash flow and operational
                efficiency.
              </Paragraph>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Row gutter={[16, 16]}>
                {services.map((feature, index) => (
                  <Col xs={24} sm={12} md={12} lg={12} key={index}>
                    <Card
                      hoverable
                      className="effectiveCard"
                      style={{
                        display: 'flex',
                        alignItems: "center",
                        flexDirection: "column",
                        justifyContent: "center",
                        padding: "20px",
                        minHeight: "180px",
                        background: "#FFF",
                        boxShadow: "0 0 30px 0 rgba(186, 184, 191, 0.35)",
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'center' }} className="inventoryIcon">
                        <img src={image1} alt="Image-1" />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <Title
                          level={4}
                          style={{
                            marginTop: "10px",
                            color: "#000",
                            fontWeight: "normal",
                            lineHeight: "1.5",
                            fontSize: "16px",
                          }}
                        >
                          {feature.title}
                        </Title>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          {/* Multi stock management */}
          <Row gutter={[32, 32]} className="mt-5">
            {/* Image Column */}
            <Col
              xs={{ span: 24, order: 2 }}
              sm={{ span: 24, order: 2 }}
              md={{ span: 12, order: 1 }}
              lg={{ span: 12, order: 1 }}
            >
              <Row gutter={[16, 16]}>
                <img
                  src={invBanner}
                  alt="Placeholder Demo Image"
                  className="demo-request-image responsive-image"
                />
              </Row>
            </Col>

            {/* Text Column */}
            <Col
              xs={{ span: 24, order: 1 }}
              sm={{ span: 24, order: 1 }}
              md={{ span: 12, order: 2 }}
              lg={{ span: 12, order: 2 }}
            >
              <Title level={2}>Manage Multiple Stores</Title>
              <Paragraph
                style={{
                  fontSize: "15px",
                  color: "#555",
                  marginBottom: "10px",
                }}
              >
                Multiple store management allows businesses to track inventory, sales,
                and stock valuation across different locations. It ensures real-time
                updates, low-stock alerts, and negative stock restrictions per store.
                This system improves efficiency, prevents stockouts, and provides
                better control over stock distribution, enhancing overall business
                operations and decision-making.
              </Paragraph>
              {multipleStock.map((feature, index) => (
                <div key={index} style={{ fontSize: "16px" }}>
                  <CheckCircleFilled style={{ color: "#689F38" }} /> {feature}
                </div>
              ))}
            </Col>
          </Row>


          {/* Physical stock */}
          <Row gutter={[32, 32]} className="mt-5">
            <Col xs={24} sm={24} md={12} lg={12}>
              <Title level={2}>Physical Stock Reconciliation</Title>
              <Paragraph
                style={{
                  fontSize: "15px",
                  color: "#555",
                  marginBottom: "10px",
                }}
              >
                Physical stock reconciliation at the item level ensures accurate
                inventory management by comparing system records with actual
                stock counts. This process helps identify discrepancies due to
                errors, theft, or mismanagement. By reconciling stock regularly,
                businesses can maintain precise inventory levels, prevent stock
                shortages, and improve financial reporting. It also enhances
                decision-making by providing real-time insights into stock
                movements. Efficient reconciliation minimizes losses, optimizes
                stock allocation across multiple stores, and ensures compliance
                with accounting standards. Implementing a structured
                reconciliation process boosts operational efficiency, reduces
                discrepancies, and enhances overall store management, leading to
                better resource utilization and profitability.
              </Paragraph>
              {physicalStock.map((feature, index) => (
                <div key={index} style={{ fontSize: "16px" }}>
                  <CheckCircleFilled style={{ color: "#689F38" }} /> {feature}
                </div>
              ))}
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Row gutter={[16, 16]}>
                <img
                  src={inventoryBanner}
                  alt="Placeholder Demo Image"
                  className="demo-request-image responsive-image"
                />
              </Row>
            </Col>
          </Row>

          {/* custom fields */}
          <Row gutter={[32, 32]} className="mt-5">
            {/* First Column (Image) */}
            <Col
              xs={{ span: 24, order: 2 }} // 👈 Will appear second on mobile
              sm={{ span: 24, order: 2 }}
              md={{ span: 12, order: 1 }} // 👈 Normal order from tablet onwards
              lg={{ span: 12, order: 1 }}
            >
              <Row gutter={[16, 16]}>
                <img
                  src={featured}
                  alt="Placeholder Demo Image"
                  className="demo-request-image responsive-image"
                />
              </Row>
            </Col>

            {/* Second Column (Text) */}
            <Col
              xs={{ span: 24, order: 1 }} // 👈 Will appear first on mobile
              sm={{ span: 24, order: 1 }}
              md={{ span: 12, order: 2 }} // 👈 Normal order from tablet onwards
              lg={{ span: 12, order: 2 }}
            >
              <Title level={2}>Custom Fields and Alternate Units</Title>
              <Paragraph
                style={{
                  fontSize: "15px",
                  color: "#555",
                  marginBottom: "10px",
                }}
              >
                Custom fields and alternate units at the item level enhance store
                management by allowing businesses to tailor inventory tracking to their
                needs. Custom fields help store additional item-specific details,
                improving categorization and reporting. Alternate units enable seamless
                conversions between different measurement units (e.g., pieces to boxes),
                ensuring accurate stock control. These features improve flexibility,
                streamline operations, and support better decision-making. By customizing
                item attributes and managing units efficiently, businesses can optimize
                inventory processes, reduce errors, and enhance overall store performance.
                This system ensures adaptability for various industries, making inventory
                management more precise and efficient.
              </Paragraph>
              {customField.map((feature, index) => (
                <div key={index} style={{ fontSize: "16px" }}>
                  <CheckCircleFilled style={{ color: "#689F38" }} /> {feature}
                </div>
              ))}
            </Col>
          </Row>


          {/* Stock history */}
          <Row gutter={[32, 32]} className="mt-5">
            <Col xs={24} sm={24} md={12} lg={12}>
              <Title level={2}>Stock History</Title>
              <Paragraph
                style={{
                  fontSize: "15px",
                  color: "#555",
                  marginBottom: "10px",
                }}
              >
                Stock history tracking at both the item and store levels
                provides a detailed record of inventory movements, ensuring
                transparency and accuracy. It logs item transfers between
                stores, helping businesses monitor stock allocation, prevent
                shortages, and optimize distribution. By maintaining a history
                of stock movements, businesses can analyze trends, detect
                discrepancies, and improve decision-making. This feature
                enhances accountability, reduces losses, and ensures real-time
                visibility into stock flow. Efficient stock history management
                improves inventory control, supports auditing, and helps
                businesses respond proactively to demand fluctuations,
                ultimately leading to better operational efficiency and resource
                utilization across multiple store locations.
              </Paragraph>
              {stockHistory.map((feature, index) => (
                <div key={index} style={{ fontSize: "16px" }}>
                  <CheckCircleFilled style={{ color: "#689F38" }} /> {feature}
                </div>
              ))}
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Row gutter={[16, 16]}>
                <img
                  src={inventoryControl}
                  alt="Placeholder Demo Image"
                  className="demo-request-image responsive-image"
                />
              </Row>
            </Col>
          </Row>

          {/* Call to Action */}
          <Card
            bordered={false}
            className="card-default salesFeature-card-footer"
          >
            <Title level={2}>
              Start Managing Your Inventory Efficiently Today!
            </Title>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request demo
              </Button>
            </Link>
          </Card>
        </Content>
      </Layout>
      <Footer />
    </div>
  );
};

export default InventoryFeature;
